"use client";

import { useState, useRef, useEffect } from "react";
import { useDismissable } from "@/lib/hooks/useDismissable";
import { useModalSurface } from "@/lib/hooks/useModalSurface";
import { useSiteSearch } from "@/lib/hooks/useSiteSearch";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { usePathname } from "next/navigation";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { useMyGroups } from "@/lib/hooks/useMyGroups";
import { useNotifications, useUnreadNotificationCount, useMarkNotificationRead, useMarkAllNotificationsRead, notificationsOf } from "@/lib/hooks/useNotifications";
import { CountBadge, UnreadMarker } from "@/components/UnreadMarker";
import { useHasUnseenDmActivity, useUnreadMessageCount } from "@/lib/hooks/useConversations";
import { DmBadge } from "@/components/DmSignal";
import { MessagesDropdown } from "@/components/MessagesDropdown";
import { useFeedback } from "@/components/FeedbackButton";
import { TimeAgo } from "./TimeAgo";
import { useQueryClient } from "@tanstack/react-query";
import { logout } from "@/lib/api/session";
import { Avatar } from "./ui/Avatar";
import { AvatarStack } from "./AvatarStack";
import { Input } from "./ui/Input";
import { SearchResultRow } from "./SearchResultRow";
import { activeGroupSlug } from "@/lib/groupSlug";

/**
 * Routes that carry their OWN Theta mark and must not get the bar's as well.
 *
 * These are the standalone token-landing screens: an invitation, an open
 * invite link, an activation link. Each is a page somebody arrives at from an
 * e-mail with one thing to do, and each renders the ident through
 * `CenteredCard` — so the bar's small mark above it was the same logo twice.
 * The bar's "Log ind" is no loss either: `InviteLinkClient` already handles
 * the signed-in and the has-an-account reader inside the card.
 */
const HIDDEN_PATHS = ["/login", "/invitationer", "/u/activate-account"];

/** The same ceiling `GroupSidebar` puts on the desktop list, for the same
    reason: past ten rows the drawer is a scroll, not a menu. */
const MAX_DRAWER_GROUPS = 10;


export function SiteHeader() {
  const pathname = usePathname();
  const { data: user, isLoading } = useCurrentUser();
  const unreadCount = useUnreadNotificationCount();
  const unreadMessages = useUnreadMessageCount();
  /**
   * A rename or a reaction on one of your messages — OSO-549/550. Not a
   * number, and deliberately not in the bell: the DM inbox is where a
   * conversation event belongs. See `DmSignal`.
   */
  const dmActivity = useHasUnseenDmActivity();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  /**
   * OUTSIDE-CLICK, BUT NOT ESCAPE (OSO-637). Before this the menu was opened
   * by `onMouseEnter` and closed by `onMouseLeave`, so leaving the pointer was
   * the only dismissal there was and no outside handler was needed. Now that a
   * click can open it, one is: a pointer that never entered the menu can never
   * leave it.
   *
   * `escape: false` because this trigger has somewhere to put focus afterwards
   * and the shared handler does not — see `handleDropdownKeyDown`.
   */
  const dropdownRef = useDismissable<HTMLDivElement>(
    dropdownOpen,
    () => setDropdownOpen(false),
    { escape: false },
  );
  const avatarButtonRef = useRef<HTMLButtonElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useDismissable<HTMLDivElement>(notifOpen, () => setNotifOpen(false), {
    escape: false,
  });
  // Signed out, there is no bell and no answer: the route 401s for an
  // anonymous caller, so polling it is five requests a minute that cannot
  // succeed (OSO-575).
  const { data: notifData } = useNotifications({ enabled: !!user });
  const notifications = notificationsOf(notifData);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  /**
   * THE PRIMARY NAVIGATION ON A PHONE, AND IT WAS NOT A DIALOG (OSO-638).
   * Enter on the hamburger opened the drawer and left focus on the hamburger,
   * OUTSIDE it: Tab then walked into the page behind the backdrop — the Theta
   * logo, the header links — and never once into the drawer, and Escape did
   * nothing at all. The only dismissal was a tap on the backdrop `div`, which
   * has no keyboard equivalent.
   *
   * The hook supplies the whole contract, including returning focus to the
   * hamburger on close. It is the same one `ui/Dialog` uses; the drawer keeps
   * its own chrome because a full-height edge-anchored panel is not a centred
   * box with different padding.
   */
  const drawerRef = useModalSurface<HTMLElement>({
    open: sidebarOpen,
    onClose: () => setSidebarOpen(false),
  });

  /**
   * A DRAWER THAT IS NO LONGER DRAWN MUST NOT STILL HOLD THE KEYBOARD.
   *
   * The panel is `lg:hidden`, so widening the window past 1024px — a rotation,
   * a split-screen change, dragging a desktop window wider — leaves it
   * `display: none` while `sidebarOpen` is still true. The trap keeps running
   * over an invisible panel: focus is on `body`, so every Tab is swallowed and
   * redirected into a hidden element that cannot take it, and keyboard
   * navigation dies page-wide with nothing on screen to explain why. Only a
   * blind Escape recovers it.
   *
   * `matchMedia` and not a resize listener: this fires twice per crossing
   * rather than on every pixel of a drag. `lg` is Tailwind's 64rem, spelled
   * here because CSS is the only other place it exists.
   */
  useEffect(() => {
    if (!sidebarOpen) return;
    // `matchMedia` can be absent or throw in an old embedded WebView — the
    // same hazard `app/layout.tsx`'s boot script guards for. Losing this
    // cleanup there means the drawer behaves exactly as it did before this
    // effect existed, which is the right way to lose it.
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(min-width: 64rem)");
    if (mq.matches) {
      // Already past the breakpoint when the effect runs — the drawer was
      // opened on a phone and the window was resized before this mounted, or
      // React re-ran the effect after one. Closing here is the whole point of
      // the effect rather than a state-sync smell: the alternative is a panel
      // that is not drawn and still eats every Tab.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSidebarOpen(false);
      return;
    }
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setSidebarOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [sidebarOpen]);
  const [groupsExpanded, setGroupsExpanded] = useState(false);
  /**
   * THE ONLY ROUTE TO YOUR OWN GROUPS ON A PHONE.
   *
   * `GroupSidebar` — the card that lists them on desktop — is inside a
   * `hidden lg:block` column, so under `lg` "Grupper" led to the discovery
   * page and a member had no way to reach a group they had already joined
   * without searching for it again.
   *
   * FETCHED WHEN THE DRAWER OPENS, not on mount: this is one request per
   * signed-in reader on every route, and the pages that also render a group
   * sidebar share the query key, so opening the drawer there costs nothing.
   * `enabled` keeps the signed-out reader out of the 401 loop OSO-580 fixed —
   * the hamburger is behind `user` too, but the hook is not.
   */
  const { data: myGroups, isLoading: myGroupsLoading } = useMyGroups({
    enabled: !!user && sidebarOpen,
  });
  const { openFeedback, feedbackAvailable } = useFeedback();

  const handleMouseEnter = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => setDropdownOpen(false), 150);
  };

  /**
   * Escape closes and PUTS FOCUS BACK ON THE TRIGGER. Without the second half
   * a keyboard reader who dismisses the menu is returned to the top of the
   * document and has to tab the whole header again — which is the same cost
   * the skip link exists to remove.
   *
   * On the container rather than the button, so it answers whether focus sits
   * on the avatar or on one of the links inside the open panel.
   */
  const handleDropdownKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Escape" || !dropdownOpen) return;
    e.preventDefault();
    setDropdownOpen(false);
    avatarButtonRef.current?.focus();
  };

  const router = useRouter();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDismissed, setSearchDismissed] = useState(false);
  // ONE RANKED LIST from `GET /search` (§2.1) — the header and `/soeg` read the
  // same hook, so they cannot disagree about what a result is or where it goes.
  const { results: searchResults } = useSiteSearch(searchQuery);
  const searchOpen = !searchDismissed && searchResults.length > 0;
  const searchRef = useDismissable<HTMLDivElement>(searchOpen, () => setSearchDismissed(true), {
    escape: false,
  });

  // Close the sidebar on navigation. Adjusted during render rather than in an
  // effect so it's already closed in the commit that paints the new route —
  // an effect closes it one render later, briefly showing the new page with
  // the old sidebar still open.
  const [prevPathname, setPrevPathname] = useState(pathname);
  if (prevPathname !== pathname) {
    setPrevPathname(pathname);
    setSidebarOpen(false);
  }

  function handleSearchChange(value: string) {
    setSearchQuery(value);
    setSearchDismissed(false);
  }

  if (HIDDEN_PATHS.some((p) => pathname.startsWith(p))) return null;

  async function handleLogout() {
    setDropdownOpen(false);
    // `DELETE /sessions/current` revokes THE ROW THIS REQUEST AUTHENTICATED
    // WITH, whichever carrier it arrived on. There is no handle in the path —
    // the legacy `DELETE /session/{username}` had one, and the session cookie
    // is HttpOnly so the client could never have checked they matched.
    await logout();
    queryClient.clear();
    router.push("/login");
    router.refresh();
  }

  const avatarUrl = user
    ? user.avatar_template.replace("{size}", "48")
    : undefined;

  // Ensure avatar URL is absolute or proxied
  const resolvedAvatar =
    avatarUrl && avatarUrl.startsWith("//")
      ? `http:${avatarUrl}`
      : avatarUrl ?? undefined;

  return (
    <>
    <header className="bg-surface border-b border-border sticky top-0 z-50">
      <div className="relative max-w-[1400px] mx-auto px-4 flex items-center h-14 gap-4">
        {user && (
          <button
            className="lg:hidden p-2.5 -ml-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
            onClick={() => setSidebarOpen(true)}
            aria-label="Menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        )}

        {/* Logo — icon always visible, "Theta" text slides in on hover */}
        <Link href="/feed" className="shrink-0 group flex items-center gap-0 overflow-hidden">
          <img
            src="/favicon.png"
            alt="Theta"
            className="w-7 h-7 shrink-0 dark:invert"
          />
          <span
            className="text-2xl tracking-tight text-foreground max-w-0 group-hover:max-w-[120px] overflow-hidden whitespace-nowrap transition-all duration-300 ease-in-out opacity-0 group-hover:opacity-100 ml-0 group-hover:ml-1.5"
            style={{ fontFamily: "Neureal, sans-serif" }}
          >
            Theta
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-2">
          <Link
            href="/feed"
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
            title="Hjem"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </Link>
        </nav>

        {/* Search — desktop only.

            OUT OF THE FLEX FLOW, because the header's two sides are not the
            same width and never will be: `mx-auto` on a flex child centres it
            in the slack LEFT OVER in the row, not in the header, so the field
            sat off-centre — and the logo's wordmark animating
            `max-w-0 → max-w-[120px]` on hover widened the left block, sliding
            the field sideways while the pointer rested on the logo (OSO-488).
            Centring against the header itself is immune to both. Still the
            positioned ancestor the results dropdown below anchors to.

            SIGNED IN ONLY (OSO-498). `GET /search` answers for the viewer, so
            to a logged-out visitor the field promised a search it could not
            run. The mobile icon below was already inside the `user` branch;
            this one sat outside it, which is the whole bug. */}
        {user && (
        <div
          className="hidden lg:block absolute left-1/2 -translate-x-1/2 w-full max-w-md"
          ref={searchRef}
        >
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle-foreground"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <Input
              type="search"
              name="q"
              autoComplete="off"
              placeholder="Søg..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => setSearchDismissed(false)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-muted dark:text-gray-100 rounded-full border border-transparent focus:border-indigo-300 dark:focus:border-indigo-600 focus:bg-surface focus:outline-none transition-colors"
            />
          </div>
          {searchOpen && (
            <div className="absolute top-full mt-1 left-0 right-0 bg-surface rounded-xl border border-border shadow-lg z-50 py-1 max-h-60 sm:max-h-80 overflow-y-auto">
              {searchResults.map((result) => (
                <SearchResultRow
                  key={`${result.resultType}-${result.id}`}
                  result={result}
                  onNavigate={() => {
                    setSearchDismissed(true);
                    setSearchQuery("");
                  }}
                />
              ))}
            </div>
          )}
        </div>
        )}

        <div className="flex items-center gap-1 sm:gap-3 shrink-0 ml-auto">
          {isLoading ? (
            <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
          ) : user ? (
            <>
            <Link
              href="/soeg"
              className="lg:hidden p-2.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
              title="Søg"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </Link>

            <Link
              href="/beskeder"
              className="lg:hidden relative p-2.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
              title="Beskeder"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
              <DmBadge
                count={unreadMessages}
                activity={dmActivity}
                className="absolute -top-0.5 -right-0.5"
              />
            </Link>

            <MessagesDropdown unreadMessages={unreadMessages} dmActivity={dmActivity} />

            {/* Notification bell — mobile: link, desktop: dropdown */}
            <Link
              href="/notifikationer"
              className="lg:hidden relative p-2.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
              title="Notifikationer"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              <CountBadge count={unreadCount} className="absolute -top-0.5 -right-0.5" />
            </Link>

            <div className="hidden lg:block relative" ref={notifRef}>
              <button
                onClick={() => setNotifOpen((prev) => !prev)}
                className="relative p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                title="Notifikationer"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                <CountBadge count={unreadCount} className="absolute -top-0.5 -right-0.5" />
              </button>

              {notifOpen && (
                /* w-96, not w-80 (OSO-605). The panel only ever opens at `lg`
                   and up, and 64px of extra room is what takes the longest
                   sentences — the ones naming three people — from three
                   wrapped lines down to two. */
                <div className="absolute right-0 top-full mt-1 w-96 bg-surface rounded-xl border border-border shadow-lg z-50">
                  <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    {/* THE UNREAD TOTAL, SPELLED OUT (OSO-524, OSO-586).
                        It is a count of ROWS now, so it is countable from the
                        list — but this dropdown is capped and ordered by time,
                        so an older unread row can still sit below its fold.
                        Saying the number here means the badge is explicable
                        without the member having to reconcile it against the
                        few rows this panel has space for. */}
                    <h3 className="text-sm font-semibold text-foreground">
                      Notifikationer
                      {unreadCount > 0 && (
                        <span className="ml-1.5 font-normal text-muted-foreground">
                          ({unreadCount > 99 ? "99+" : unreadCount} ulæste)
                        </span>
                      )}
                    </h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={() => markAllRead()}
                        className="text-xs text-primary hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
                      >
                        Marker alle som læst
                      </button>
                    )}
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="px-4 py-6 text-sm text-muted-foreground text-center">
                        Ingen notifikationer endnu
                      </p>
                    ) : (
                      /* `n.text` and `n.href` are SERVER-RENDERED (§1.12) —
                         the formatter and the href cascade that used to build
                         them on the client are deleted. */
                      notifications.slice(0, 10).map((n) => (
                        <Link
                          key={n.id}
                          href={n.href}
                          onClick={() => {
                            setNotifOpen(false);
                            if (!n.isRead && n.can.markRead) markRead(n.id, n.groupedIds);
                          }}
                          className={`flex items-center gap-3 px-4 py-3 hover:bg-surface-2 transition-colors ${!n.isRead ? "bg-indigo-50/50 dark:bg-indigo-950/50" : ""}`}
                        >
                          <AvatarStack
                            size="xs"
                            primaryUrl={n.actor?.avatar.small}
                            primaryName={n.actor?.name || "?"}
                            otherUrls={n.otherActors.map((a) => a.avatar.small)}
                            otherNames={n.otherActors.map((a) => a.name ?? "")}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground">{n.text}</p>
                            {/* THE TIME SITS BESIDE THE QUOTE, NOT UNDER IT
                                (OSO-605). Two ornaments, three lines: the
                                sentence already wraps, and giving "13m" a line
                                of its own made every row with an excerpt five
                                lines tall. `min-w-0` on the quote so the
                                truncation still happens there and never eats
                                the timestamp. */}
                            <div className="flex items-baseline gap-2 mt-0.5">
                              <TimeAgo date={n.createdAt} />
                              {n.excerpt && (
                                <p className="min-w-0 flex-1 text-xs text-subtle-foreground truncate">
                                  &ldquo;{n.excerpt}&rdquo;
                                </p>
                              )}
                            </div>
                          </div>
                          <UnreadMarker count={n.isRead ? 0 : n.unreadCount} />
                        </Link>
                      ))
                    )}
                  </div>
                  <div className="border-t border-border">
                    <Link
                      href="/notifikationer"
                      onClick={() => setNotifOpen(false)}
                      className="block px-4 py-2.5 text-sm text-center text-primary hover:bg-surface-2 transition-colors"
                    >
                      Se alle
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <div
              className="relative"
              ref={dropdownRef}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onKeyDown={handleDropdownKeyDown}
            >
              {/* A REAL DISCLOSURE, NOT A HOVER TARGET (OSO-637).
                  Hover stays as the accelerator it always was, but it used to
                  be the ONLY opener: the button carried no onClick and no key
                  handler, which on desktop made Min profil, Indstillinger, Om
                  Theta, Admin and Log ud unreachable without a mouse — this
                  menu is the only route to all five.

                  The accessible name was "S", the letter avatar's initial, so
                  a screen reader announced "S, knap" and pressing it did
                  nothing. `aria-label` names what the button opens instead. */}
              <button
                type="button"
                ref={avatarButtonRef}
                onClick={() => setDropdownOpen((open) => !open)}
                aria-label="Din konto og indstillinger"
                aria-haspopup="menu"
                aria-expanded={dropdownOpen}
                aria-controls="brugermenu"
                className="flex items-center gap-2 rounded-full hover:bg-muted p-1 transition-colors"
              >
                <Avatar
                  url={resolvedAvatar}
                  name={user.name || user.username}
                  size="sm"
                />
              </button>

              {dropdownOpen && (
                <div id="brugermenu" className="absolute right-0 top-full mt-1 w-56 bg-surface rounded-xl border border-border shadow-lg py-1 z-50">
                  <div className="px-4 py-3 border-b border-border">
                    <p className="text-sm font-semibold text-foreground truncate">
                      {user.name || user.username}
                    </p>
                  </div>
                  <Link
                    href={`/profil/${user.id}`}
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-surface-2 transition-colors"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-subtle-foreground"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    Min profil
                  </Link>
                  <Link
                    href="/beskeder"
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-surface-2 transition-colors"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-subtle-foreground"
                    >
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                    Beskeder
                    <DmBadge count={unreadMessages} activity={dmActivity} className="ml-auto" />
                  </Link>
                  <Link
                    href="/indstillinger"
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-surface-2 transition-colors"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-subtle-foreground"
                    >
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                    Indstillinger
                  </Link>
                  <Link
                    href="/om"
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-surface-2 transition-colors"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-subtle-foreground"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    Om Theta
                  </Link>
                  {user.admin && (
                    <Link
                      href="/admin"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-surface-2 transition-colors"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-subtle-foreground"
                      >
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                      </svg>
                      Admin
                    </Link>
                  )}
                  <div className="border-t border-border mt-1 pt-1">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-destructive hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      Log ud
                    </button>
                  </div>
                </div>
              )}
            </div>
            </>
          ) : (
            <Link
              href="/login"
              className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Log ind
            </Link>
          )}
        </div>
      </div>
    </header>

    {sidebarOpen && (
      <>
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-50 animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
        <nav
          ref={drawerRef}
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          tabIndex={-1}
          className="lg:hidden fixed inset-y-0 left-0 w-64 bg-surface z-50 shadow-xl flex flex-col animate-slide-in-left outline-none"
        >
          <div className="flex items-center justify-between px-4 h-14 border-b border-border">
            <span
              className="text-xl tracking-tight text-foreground"
              style={{ fontFamily: "Neureal, sans-serif" }}
            >
              Theta
            </span>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
              aria-label="Luk menu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="flex-1 py-2">
            <Link
              href="/feed"
              className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                pathname === "/feed" || pathname === "/" ? "text-primary bg-primary-muted" : "text-foreground hover:bg-surface-2"
              }`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              Hjem
            </Link>
            <Link
              href="/grupper"
              className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                pathname.startsWith("/grupper") ? "text-primary bg-primary-muted" : "text-foreground hover:bg-surface-2"
              }`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
              </svg>
              Grupper
            </Link>

            {/* Mine grupper — the drawer's answer to a sidebar the phone never
                gets. Collapsed by default so the fixed destinations below it
                stay on screen; the count sits on the row so it reads as
                something to open while it is shut. */}
            <button
              onClick={() => setGroupsExpanded((open) => !open)}
              aria-expanded={groupsExpanded}
              className="flex items-center gap-3 w-full px-4 py-3 text-sm font-medium text-foreground hover:bg-surface-2 transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              Mine grupper
              {myGroups && myGroups.length > 0 && (
                <span className="text-xs text-subtle-foreground">{myGroups.length}</span>
              )}
              <svg
                className={`ml-auto transition-transform ${groupsExpanded ? "rotate-180" : ""}`}
                width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {groupsExpanded && (
              <div className="pb-1">
                {myGroupsLoading && (
                  <p className="px-4 py-2 pl-12 text-sm text-subtle-foreground">Henter…</p>
                )}
                {!myGroupsLoading && myGroups?.length === 0 && (
                  <p className="px-4 py-2 pl-12 text-sm text-subtle-foreground">
                    Du er ikke med i nogen grupper endnu.
                  </p>
                )}
                {myGroups?.slice(0, MAX_DRAWER_GROUPS).map((group) => {
                  const href = `/grupper/${group.slug}`;
                  return (
                    <Link
                      key={group.id}
                      href={href}
                      /** `page` only when the row IS the page — see `GroupAdminSidebar`. */
                      aria-current={pathname === href ? "page" : undefined}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 py-2 pl-12 pr-4 text-sm transition-colors ${
                        activeGroupSlug(pathname) === group.slug
                          ? "text-primary bg-primary-muted"
                          : "text-foreground hover:bg-surface-2"
                      }`}
                    >
                      {group.coverImageUrl ? (
                        <img src={group.coverImageUrl} alt="" className="w-6 h-6 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className="w-6 h-6 rounded-lg bg-primary-muted/50 flex items-center justify-center text-primary font-bold text-[0.625rem] shrink-0">
                          {group.name[0]}
                        </div>
                      )}
                      <span className="flex-1 min-w-0 truncate">{group.name}</span>
                      <CountBadge count={group.unreadCount} label={`${group.unreadCount} ulæste`} />
                    </Link>
                  );
                })}
                {(myGroups?.length ?? 0) > MAX_DRAWER_GROUPS && (
                  <Link
                    href="/grupper/mine"
                    onClick={() => setSidebarOpen(false)}
                    className="block py-2 pl-12 pr-4 text-xs text-subtle-foreground hover:text-foreground transition-colors"
                  >
                    Vis alle {myGroups?.length} grupper
                  </Link>
                )}
              </div>
            )}
            <Link
              href="/beskeder"
              className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                pathname.startsWith("/beskeder") ? "text-primary bg-primary-muted" : "text-foreground hover:bg-surface-2"
              }`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
              Beskeder
              <DmBadge count={unreadMessages} activity={dmActivity} className="ml-auto" />
            </Link>
            <Link
              href="/begivenheder"
              className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                pathname.startsWith("/begivenheder") ? "text-primary bg-primary-muted" : "text-foreground hover:bg-surface-2"
              }`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              Begivenheder
            </Link>
            <Link
              href="/grupper/opret"
              className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                pathname === "/grupper/opret" ? "text-primary bg-primary-muted" : "text-foreground hover:bg-surface-2"
              }`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
              Opret gruppe
            </Link>
            <Link
              href="/soeg"
              className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                pathname === "/soeg" ? "text-primary bg-primary-muted" : "text-foreground hover:bg-surface-2"
              }`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              Søg
            </Link>
          </div>

          {/* Feedback lives here on mobile — as a floating button it covered
              content in the bottom-right corner. */}
          {feedbackAvailable && (
            <div className="border-t border-border py-2">
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  openFeedback();
                }}
                className="flex items-center gap-3 w-full px-4 py-3 text-sm font-medium text-foreground hover:bg-surface-2 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Giv feedback
              </button>
            </div>
          )}
        </nav>
      </>
    )}
    </>
  );
}
